import React, { useState } from "react";
import classNames from "classnames";
import { useQuery } from "@tanstack/react-query";
import { getAddressQueryKey } from "queryClient";
import { getAddress } from "@lib/entity";
import { Address } from "@common/types/embeddable/address";
import { useEntity } from "../../entity/useEntity";
import { EntityType, MergedEntityType } from "../../../common/types/entity";
import { SettingRow } from "../General/SettingRow";
import styles from "./EntityGeneralSettings.module.scss";
import { UpdateNameModal } from "./Modals/UpdateNameModal";
import { UpdateWebsiteModal } from "./Modals/UpdateWebsiteModal";
import { UpdateDescriptionModal } from "./Modals/UpdateDescriptionModal";
import { UpdateAddressModal } from "./Modals/UpdateAddressModal";

const ENTITY_TYPE_LABELS: Record<MergedEntityType, string> = {
    llc: "LLC",
    c_corp: "C Corp",
    s_corp: "S Corp",
    not_registered: "Not registered (Sole proprietorship)",
    llc_s_corp: "LLC with S Corp Election",
    c_corp_with_s_corp: "C Corporation with S Corp Election",
    sole_proprietorship: "Sole Proprietorship",
    smllc: "LLC (sole owner)",
    mmllc: "LLC (multiple owners)",
    partnership: "Partnership (including LLPs)",
    no_legal_entity: "I don't have a US legal entity",
};

const EntityNameRow: React.FC<{ onEdit: () => void }> = ({ onEdit }) => {
    const entity = useEntity();
    return (
        <SettingRow
            label="Entity name"
            value={entity.name}
            buttonText="Edit name"
            onButtonClick={onEdit}
        />
    );
};

const EntityDescriptionRow: React.FC<{ onEdit: () => void }> = ({ onEdit }) => {
    const entity = useEntity();
    return (
        <SettingRow
            label="Description"
            value={entity.profile?.description}
            buttonText="Edit description"
            onButtonClick={onEdit}
        />
    );
};

const EntityWebsiteRow: React.FC<{ onEdit: () => void }> = ({ onEdit }) => {
    const entity = useEntity();
    return (
        <SettingRow
            label="Website"
            value={entity.profile?.website}
            buttonText="Edit website"
            onButtonClick={onEdit}
        />
    );
};

const EntityEntityTypeRow: React.FC = () => {
    const entity = useEntity();
    return (
        <SettingRow
            label="Entity type"
            value={
                ENTITY_TYPE_LABELS[
                    entity.profile?.type ?? EntityType.NOT_REGISTERED
                ]
            }
        />
    );
};

const createDisplayAddress = (address: Partial<Address>) => {
    let final = "";
    if (address.street) {
        final += address.street;
    }
    if (address.city) {
        final += ", " + address.city;
    }
    if (address.state) {
        final += ", " + address.state;
    }
    if (address.postalCode) {
        final += ", " + address.postalCode;
    }
    return final;
};

const EntityAddressRow: React.FC<{
    onEdit: () => void;
    address?: Partial<Address>;
}> = ({ onEdit, address }) => (
    <SettingRow
        label="Address"
        value={createDisplayAddress(address ?? {})}
        buttonText="Edit address"
        onButtonClick={onEdit}
    />
);

export const EntityGeneralSettings: React.FC = () => {
    const [showNameUpdate, setShowNameUpdate] = useState(false);
    const [showDescriptionUpdate, setShowDescriptionUpdate] = useState(false);
    const [showWebsiteUpdate, setShowWebsiteUpdate] = useState(false);
    const [showAddressUpdate, setShowAddressUpdate] = useState(false);

    const entity = useEntity();
    const address = useQuery({
        queryKey: getAddressQueryKey(entity.id),
        queryFn: () => getAddress(entity),
    });

    return (
        <section className={classNames("mb-5", styles.container)}>
            <header className="settings__section__header">
                <h4>
                    {entity.isBusiness ? "Business" : "Personal"} information
                </h4>
            </header>

            <div className={styles.settingsContainer}>
                {entity.isBusiness && (
                    <>
                        <EntityNameRow onEdit={() => setShowNameUpdate(true)} />
                        <EntityEntityTypeRow />
                        <EntityDescriptionRow
                            onEdit={() => setShowDescriptionUpdate(true)}
                        />
                        <EntityWebsiteRow
                            onEdit={() => setShowWebsiteUpdate(true)}
                        />
                    </>
                )}
                <EntityAddressRow
                    onEdit={() => setShowAddressUpdate(true)}
                    address={address.data ?? undefined}
                />
            </div>

            <UpdateNameModal
                show={showNameUpdate}
                onClose={() => setShowNameUpdate(false)}
            />
            <UpdateWebsiteModal
                show={showWebsiteUpdate}
                onClose={() => setShowWebsiteUpdate(false)}
            />
            <UpdateDescriptionModal
                show={showDescriptionUpdate}
                onClose={() => setShowDescriptionUpdate(false)}
            />
            <UpdateAddressModal
                show={showAddressUpdate}
                address={address.data ?? undefined}
                onClose={() => setShowAddressUpdate(false)}
            />
        </section>
    );
};
